import axios from 'axios';
import {
    LMS_CHAT_URL,
    query, queryBot, queryBot2, queryBot2Many,
} from 'boot/axios';
import ShortUniqueId from 'short-unique-id';
import { UserStatus } from 'src/model/partner.log';
import unescapeSql from 'src/mixins/utils';
import { DIGITAL_TRACE_STATUS } from 'pages/sot.bot.admin/digitalTraces/digitalTraces';
import { accessInfo } from 'src/model/access';

const moment = require('moment');

// eslint-disable-next-line import/prefer-default-export
export class Users {
    constructor (app) {
        this.app = app;
        this.error = undefined;
        this.context = this.app.config.globalProperties;
        this.$q = this.context.$q;
    }

    update (user) {
        this.error = undefined;
        return new Promise((resolve, reject) => {
            const sql = 'update users set '
                + ` shownname = '${unescapeSql(user.shownname)}', `
                + ` region_name = '${unescapeSql(user.region_name)}', `
                + ` region_number = '${unescapeSql(user.region_number)}', `
                + ` school_number = '${unescapeSql(user.school_number)}', `
                + ` position = '${unescapeSql(user.position)}', `
                + ` bio = '${unescapeSql(user.bio)}', `
                + ` tel = '${unescapeSql(user.tel)}', `
                + ` chat_channel = '${user.chat_channel}' `
                + ` where id = ${user.id}`;
            queryBot(sql).then((r) => {
                if (r.data.id === 'OK') {
                    resolve(user);
                } else {
                    reject(r.data);
                }
            }).catch((e) => {
                this.error = e;
                reject(e.toString());
            });
        });
    }

    create (user) {
        this.error = undefined;
        const chatId = new ShortUniqueId({ length: 16 })();

        return new Promise((resolve, reject) => {
            const sql = 'insert into users ('
                    + ' shownname, '
                    + ' region_name, '
                    + ' region_number, '
                    + ' school_number, '
                    + ' position, '
                    + ' chat_id, '
                    + ' tel, '
                    + ' disable_sending '
                    + ' ) values ('
                    + ` '${user.shownname}', `
                    + ` '${user.region_name}', `
                    + ' "", '
                    + ` '${user.school_number}', `
                    + ` '${user.position}', `
                    + ` '${chatId}', `
                    + ` '${user.tel}', `
                    + ` ${'1'} `
                    + ' )';

            queryBot(sql).then((r) => {
                this.removeFromPreregister(user.tel);
                if (r.data.id) {
                    user.id = r.data.id;
                    user.chat_id = chatId;
                    user.stars = 0;
                    user.extra_stars = 0;
                    resolve(user);
                } else {
                    console.error(r);
                    reject(r.data);
                }
            }).catch((e) => {
                this.error = e;
                console.error(e);
                reject(e.toString());
            });
        });
    }

    findUser (filter) {
        return new Promise((resolve, reject) => {
            if (filter) {
                filter = `where ${filter}`;
            }
            queryBot(`select * from users ${filter}`).then((r) => {
                resolve(r.data[0]);
            }).catch((e) => {
                this.error = e;
                console.error(e);
                reject(e.toString());
            });
        });
    }

    // eslint-disable-next-line class-methods-use-this
    removeFromPreregister (tel) {
        query(`delete from preregister where tel = '${tel}'`);
    }

    getUserChatTaskStars (userId, taskId) {
        this.error = undefined;
        return new Promise((resolve, reject) => {
            const sql = 'select sum(stars_count) from chat '
                + ' where has_star = 1 and '
                + `user_id = '${userId}' and `
                + `task_id = '${taskId}'`;
            queryBot(sql).then((r) => {
                const row = r.data[0];
                if (row) {
                    resolve(row['sum(stars_count)']);
                } else {
                    resolve(0);
                }
            }).catch((e) => {
                this.error = e;
                console.error(e);
                reject(e.toString());
            });
        });
    }

    getLastUserChatTaskAnswer (userId, taskId, fromDate) {
        this.error = undefined;
        return new Promise((resolve, reject) => {
            queryBot2('get-last-chat-answer', { chatId: userId, taskId, createdAt: fromDate }).then((r) => {
                const row = r.data[0];
                if (row) {
                    resolve(row.message);
                } else {
                    resolve();
                }
            }).catch((e) => {
                this.error = e;
                console.error(e);
                reject(e.toString());
            });
        });
    }

    getBalance (userId) {
        return new Promise((resolve, reject) => {
            queryBot(`select balance from users where id = '${userId}'`).then((r) => {
                if (r.data[0]) {
                    resolve(r.data[0].balance);
                } else {
                    reject('User not found, id:', userId);
                }
            }).catch((e) => {
                this.error = e;
                console.error(e);
                reject(e.toString());
            });
        });
    }

    addBalanceTransaction (userId, amount, title, productType, productId, type) {
        return new Promise((resolve, reject) => {
            this.getBalance(userId).then((balance) => {
                const sql = 'INSERT INTO balance_transactions (user_id, amount, type, title, product_id, product_type, balance_after) VALUES ( '
                    + ` ${userId}, `
                    + ` '${amount}', `
                    + ` ${type}, `
                    + ` '${unescapeSql(title)}', `
                    + ` ${productId}, `
                    + ` ${productType}, `
                    + ` '${balance}')`;
                queryBot(sql).then((r) => {
                    if (r.data.id) {
                        resolve(true);
                    } else {
                        reject('addBalanceTransaction error:', r);
                    }
                }).catch((e) => {
                    this.error = e;
                    console.error(e);
                    reject(e.toString());
                });
            });
        });
    }

    addToMyProducts (userId, productType, productId, price, validUntilDate = undefined, tariff = undefined, enrollId) {
        return new Promise((resolve, reject) => {
            let sql = 'INSERT INTO user_products (user_id, enroll_id, type, product_id, price, tariff, valid_until_date) VALUES ( '
                + ` ${userId}, `
                + ` ${enrollId}, `
                + ` ${productType}, `
                + ` ${productId}, `
                + ` '${price}', `
                + ` '${tariff ? tariff.tariff : '1'}', `;

            if (validUntilDate) {
                sql += ` '${moment(validUntilDate).format('YYYY-MM-DD')}')`;
            } else {
                sql += ' null)';
            }
            // + ` '${validUntilDate ? moment(validUntilDate).format('YYYY-MM-DD') : ''}') `;
            queryBot(sql).then((r) => {
                if (r.data.id) {
                    resolve(true);
                } else {
                    resolve(true);
                    // reject('addToMyProducts error:', r);
                }
            }).catch((e) => {
                this.error = e;
                console.error(e);
                reject(e.toString());
            });
        });
    }

    getBoughtCourseInfo (courseId) {
        return new Promise((resolve) => {
            const { user } = this.context.$store.state.pages;
            if (!user) {
                resolve(undefined);
                return;
            }
            queryBot2('get-bought-course-info', { userId: user.id, courseId })
                .then((r) => {
                    resolve(r.data[0]);
                });
        });
    }

    sentToUserChatTechsupport (chatId, text, chatTypeId = 1) {
        const data = new FormData();
        data.append('content', text);
        data.append('chatId', chatTypeId);
        data.append('toLmsUser', chatId);

        const { operatorId } = accessInfo;
        const headers = { Authorization: `o:${operatorId}:lms` };

        return axios.post(`${LMS_CHAT_URL}/send-message`, data, { headers });
    }

    // eslint-disable-next-line class-methods-use-this
    async getUser (untiId, leaderId, email) {
        let user;
        await queryBot2('find-user', { untiId, leaderId, email }).then((r) => {
            [user] = r.data;
        });
        return user;
    }

    // eslint-disable-next-line class-methods-use-this
    async createFromUnti (u) {
        const chatId = new ShortUniqueId({ length: 8 })();

        let name = `${u.lastname || ''} ${u.firstname || ''} ${u.secondname || ''}`.trim().replace('  ', ' ');
        name = this.normalizeFio(name);

        const data = {
            username: u.username,
            shownname: name,
            email: u.email,
            chatId,
            untiId: u.unti_id,
            leaderId: u.leader_id,
            partnerGuid: u.partner_guid,
            hashtags: u.hashtags,
            status: UserStatus.New,
        };

        let id;
        await queryBot2('create-user-from-unti', data).then((r) => {
            id = r.data.id;
        });

        return [id, chatId];
    }

    // eslint-disable-next-line class-methods-use-this
    async updateFromUnti (chatId, u, updateUntiId = false) {
        let name = `${u.lastname} ${u.firstname} ${u.secondname}`.trim().replace('  ', ' ');
        name = this.normalizeFio(name);

        const data = {
            chatId,
            username: u.username,
            shownname: name,
            email: u.email,
            leaderId: u.leader_id,
            hashtags: u.hashtags,
            partnerGuid: u.partner_guid,
        };

        if (updateUntiId) {
            data.untiId = u.unti_id;
        }

        await queryBot2('update-user-from-unti', data);
        return chatId;
    }

    // eslint-disable-next-line class-methods-use-this
    async getEntryTest (userId, courseId) {
        let result;
        await queryBot2('get-user-entry-test', { userId, courseId }).then((r) => {
            [result] = r.data;
        });
        return result;
    }

    // eslint-disable-next-line class-methods-use-this
    async updateEntryTestCourse (userId, oldCourseId, newCourseId, enrollId = null) {
        const enrollsConditions = enrollId ? ` AND enroll_id = ${enrollId}` : '';
        const sql = `update user_course_entry set course_id = '${newCourseId}' where user_id = '${userId}' and course_id = '${oldCourseId}'${enrollsConditions}`;
        await queryBot(sql);
        return true;
    }

    // eslint-disable-next-line class-methods-use-this
    async setEntryPassed (userId, courseId, scoreReal, scoreFinal, isAccepted, flowId, taskId = '0') {
        await queryBot(`
            insert into user_course_entry (user_id, course_id, flow_id, task_id)
            values ('${userId}', '${courseId}', '${flowId}', '${taskId}')
        `);
        const sql = `update user_course_entry set is_passed = '1', passed_at = NOW(), is_accepted = '${isAccepted}', accepted_at = NOW(), `
            + ` score_real = '${scoreReal}', score_final = '${scoreFinal}', `
            + ` task_id = '${taskId}' `
            + ` where user_id = '${userId}' and course_id = '${courseId}'`;
        await queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    async getLastCourseId (userId) {
        let courseId;
        const { user } = this.context.$store.state.pages;
        const id = userId || user.id;
        await queryBot2('get-user-last-course', { userId: id }).then((r) => {
            courseId = r.data[0] ? r.data[0].last_course_id : undefined;
        });
        return courseId;
    }

    async getCourseProgress (courseId, userOverride) {
        if (!courseId) {
            return 0;
        }
        const user = userOverride || this.context.$store.state.pages.user;
        if (!user) {
            return 0;
        }

        if (user.status === UserStatus.Diploma || user.status === UserStatus.Success) {
            return 100;
        }

        // Расчет прогресса по новому алгоритму
        const visits = await queryBot2Many('get-user-course-visits', { userId: user.id, courseId });
        const passed = visits.filter((i) => +i.passed === 1);

        // eslint-disable-next-line no-mixed-operators
        const progress = passed.length / visits.length * 100;
        return progress.toFixed(0);
    }

    async updateStatus (userId, course, force = false) {
        let user;
        if (userId) {
            await queryBot(`select id, chat_id, email, has_gosuslugi from users where id = '${userId}'`).then((r) => {
                [user] = r.data;
            });
        }
        // const { user } = this.context.$store.state.pages;
        if (!user) {
            user = this.context.$store.state.pages.user;
        }

        let status;
        let lastCourseId;
        let prevStatus;
        console.log(force);
        await queryBot(`select last_course_id from users where id = '${user.id}'`).then((r) => {
            lastCourseId = r.data[0].last_course_id;
        });
        await queryBot(`select status from user_courses where course_id = '${lastCourseId}' and user_id = '${user.id}'`).then((r) => {
            if (r.data[0]) {
                prevStatus = r.data[0].status;
            }
        });
        let progress = 0;
        if (!lastCourseId) {
            status = UserStatus.Refusal;
        } else {
            progress = await this.getCourseProgress(lastCourseId, user);
            if (progress < 99) {
                status = UserStatus.Study;
            }
            if (progress === 99) {
                status = UserStatus.Diploma;
                // progress = 100;
                /*
                const isPaid = await this.getCourseIsPaid(lastCourseId, user);
                if (isPaid === '1') {
                    status = UserStatus.Success;
                    progress = 100;
                }
                */
            }
        }
        await queryBot(`update users set last_course_progress = '${progress}' where id = '${user.id}'`);
        console.log(status);
        console.log(prevStatus);
        /*
        if (status) {
            if (+prevStatus !== +status || force) {
                await queryBot(`update users set status = '${status}' where id = '${user.id}'`);
                // todo: - отправить в партнерку новый статус на стыке пользователя и курса
                // курс может отсутствовать
                sendPartnerStatus(user, +status, course);
            }
        }
        */
    }

    // eslint-disable-next-line class-methods-use-this
    getUserCourse (userId) {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
            let lastCourseId;
            await queryBot(`select last_course_id from users where id = '${userId}'`).then((r) => {
                lastCourseId = r.data[0].last_course_id;
            });
            if (!lastCourseId) {
                resolve(undefined);
            }
            const sql = 'select courses.id, courses.title, courses.alias, courses.current_flow_num, user_courses.finance_info_json  from courses '
                + ` left join user_courses on user_courses.course_id = courses.id and user_courses.user_id = '${userId}' `
                + ` where courses.id = '${lastCourseId}'`;
            queryBot(sql).then((r) => {
                resolve(r.data[0]);
            });
        });
    }

    // eslint-disable-next-line class-methods-use-this
    loadPages () {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
            const sql = 'select * from users_pages order by title';
            queryBot(sql).then((r) => {
                resolve(r.data);
            });
        });
    }

    // eslint-disable-next-line class-methods-use-this
    loadTabs (pageId) {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
            const sql = `select * from users_tabs where users_pages_id = '${pageId}' order by title`;
            queryBot(sql).then((r) => {
                resolve(r.data);
            });
        });
    }

    // eslint-disable-next-line class-methods-use-this
    loadAnyTabs (table) {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
            const sql = `select * from ${table} order by title`;
            queryBot(sql).then((r) => {
                resolve(r.data);
            });
        });
    }

    // eslint-disable-next-line class-methods-use-this
    loadAppsTabs () {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
            const sql = 'select * from apps_tabs order by title';
            queryBot(sql).then((r) => {
                resolve(r.data);
            });
        });
    }

    // eslint-disable-next-line class-methods-use-this
    loadStorageTabs () {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
            queryBot2('get-storage-tabs').then((r) => {
                resolve(r.data);
            });
        });
    }

    // eslint-disable-next-line class-methods-use-this
    loadSchoolsTabs () {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
            const sql = 'select * from schools_tabs order by title';
            queryBot(sql).then((r) => {
                resolve(r.data);
            });
        });
    }

    // eslint-disable-next-line class-methods-use-this
    insertTab (item) {
        let expression = unescapeSql(item.expression || '');
        if (expression === 'null') {
            expression = '';
        }
        const sql = 'insert into users_tabs (title, users_pages_id, description, tags, columns_json, actions_json, expression) values '
            + ` ('${unescapeSql(item.title)}', '${item.users_pages_id}', '${unescapeSql(item.description || '')}', '${unescapeSql(item.tags || '')}', '${JSON.stringify(item.columns_json || {})}', '${JSON.stringify(item.actions_json || {})}', '${expression}') `;
        return queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    insertAppsTab (item) {
        let expression = unescapeSql(item.expression);
        if (expression === 'null') {
            expression = '';
        }
        const sql = 'insert into apps_tabs (title, description, tags, columns_json, actions_json, expression) values '
            + ` ('${unescapeSql(item.title)}', '${unescapeSql(item.description || '')}', '${unescapeSql(item.tags || '')}', '${JSON.stringify(item.columns_json || {})}', '${JSON.stringify(item.actions_json || {})}', '${expression}') `;
        return queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    insertStorageTab (item) {
        let expression = unescapeSql(item.expression);
        if (expression === 'null') {
            expression = '';
        }
        const sql = 'insert into storage_tabs (title, description, tags, columns_json, actions_json, expression) values '
            + ` ('${unescapeSql(item.title)}', '${unescapeSql(item.description || '')}', '${unescapeSql(item.tags || '')}', '${JSON.stringify(item.columns_json || {})}', '${JSON.stringify(item.actions_json || {})}', '${unescapeSql(expression)}') `;
        return queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    insertSchoolsTab (item) {
        let expression = unescapeSql(item.expression);
        if (expression === 'null') {
            expression = '';
        }
        const sql = 'insert into schools_tabs (title, description, tags, columns_json, actions_json, expression) values '
            + ` ('${unescapeSql(item.title)}', '${unescapeSql(item.description || '')}', '${unescapeSql(item.tags || '')}', '${JSON.stringify(item.columns_json || {})}', '${JSON.stringify(item.actions_json || {})}', '${unescapeSql(expression)}') `;
        return queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    updateTab (item) {
        const columnsJson = JSON.stringify(item.columns_json || {});
        const actionsJson = JSON.stringify(item.actions_json || {});
        let expression = unescapeSql(item.expression || '');
        if (expression === 'null') {
            expression = '';
        }
        const pageId = item.users_pages_id;
        let pageUpdate = ` users_pages_id = '${pageId}', `;
        if (!pageId || +pageId === 0) {
            pageUpdate = '';
        }
        const sql = 'update users_tabs set '
            + ` title = '${unescapeSql(item.title)}', `
            + ` ${pageUpdate} `
            + ` description = '${unescapeSql(item.description || '')}', `
            + ` tags = '${unescapeSql(item.tags || '')}', `
            + ` columns_json = '${columnsJson}', `
            + ` actions_json = '${actionsJson}', `
            + ` expression = '${expression}' `
            + ` where id = '${item.id}' `;
        return queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    updateAppsTab (item) {
        const columnsJson = JSON.stringify(item.columns_json || {});
        const actionsJson = JSON.stringify(item.actions_json || {});
        let expression = unescapeSql(item.expression || '');
        if (expression === 'null') {
            expression = '';
        }
        const sql = 'update apps_tabs set '
            + ` title = '${unescapeSql(item.title)}', `
            + ` description = '${unescapeSql(item.description || '')}', `
            + ` tags = '${unescapeSql(item.tags || '')}', `
            + ` columns_json = '${columnsJson}', `
            + ` actions_json = '${actionsJson}', `
            + ` expression = '${expression}' `
            + ` where id = '${item.id}' `;
        return queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    updateStorageTab (item) {
        const columnsJson = JSON.stringify(item.columns_json || {});
        const actionsJson = JSON.stringify(item.actions_json || {});
        let expression = unescapeSql(item.expression || '');
        if (expression === 'null') {
            expression = '';
        }
        const sql = 'update storage_tabs set '
            + ` title = '${unescapeSql(item.title)}', `
            + ` description = '${unescapeSql(item.description || '')}', `
            + ` tags = '${unescapeSql(item.tags || '')}', `
            + ` columns_json = '${columnsJson}', `
            + ` actions_json = '${actionsJson}', `
            + ` expression = '${expression}' `
            + ` where id = '${item.id}' `;
        return queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    updateSchoolsTab (item) {
        const columnsJson = JSON.stringify(item.columns_json || {});
        const actionsJson = JSON.stringify(item.actions_json || {});
        const sql = 'update schools_tabs set '
            + ` title = '${unescapeSql(item.title)}', `
            + ` description = '${unescapeSql(item.description || '')}', `
            + ` tags = '${unescapeSql(item.tags || '')}', `
            + ` columns_json = '${columnsJson}', `
            + ` expression = '${unescapeSql(item.expression)}', `
            + ` actions_json = '${actionsJson}' `
            + ` where id = '${item.id}' `;
        return queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    removeTab (id) {
        return queryBot(`delete from users_tabs where id = '${id}'`);
    }

    // eslint-disable-next-line class-methods-use-this
    removeAnyTab (table, id) {
        return queryBot(`delete from ${table} where id = '${id}'`);
    }

    // eslint-disable-next-line class-methods-use-this
    removeAppsTab (id) {
        return queryBot(`delete from apps_tabs where id = '${id}'`);
    }

    // eslint-disable-next-line class-methods-use-this
    removeStorageTab (id) {
        return queryBot(`delete from storage_tabs where id = '${id}'`);
    }

    // eslint-disable-next-line class-methods-use-this
    insertPage (page) {
        const sql = 'insert into users_pages (title) values '
            + ` ('${unescapeSql(page.title)}') `;
        return queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    updatePage (page) {
        const sql = 'update users_pages set '
            + ` title = '${unescapeSql(page.title)}' `
            + ` where id = '${page.id}' `;
        return queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    removePage (id) {
        return queryBot(`delete from users_pages where id = '${id}'`);
    }

    // eslint-disable-next-line class-methods-use-this
    async updateUserCoursePrice (userId, courseId, category, paid) {
        const { $db } = this.context;
        const price = await $db.tasks.getCoursePrice(courseId);
        const partner = await this.getUserPartner(userId);
        let discount = 0;
        let sponsor = 0;

        if (category) {
            const cats = category.split('.');
            if (cats.length === 2) {
                discount = (price * +cats[1]) / 100;
            }
        }

        if (partner && partner.super_partner_name === 'РСО') {
            // Значит спонсор "Альмира"
            sponsor = price - discount;
        }

        const toPay = price - discount - sponsor;

        // SELECT * FROM `user_course_price`
        // id 	created_at 	user_id 	course_id 	price 	discount 	sponsor 	to_pay 	paid
        // пересчитывать в UserCourseInfo и в каком то миграторе
        const sql = 'insert into user_course_price '
            + ' (user_id, course_id, price, discount, sponsor, to_pay, paid) '
            + ' values ( '
            + ` '${userId}', `
            + ` '${courseId}', `
            + ` '${price}', `
            + ` '${discount}', `
            + ` '${sponsor}', `
            + ` '${toPay}', `
            + ` '${paid}' `
            + ') on duplicate key update '
            + ` price = '${price}', `
            + ` discount = '${discount}', `
            + ` sponsor = '${sponsor}', `
            + ` to_pay = '${toPay}', `
            + ` paid = '${paid}' `;
        await queryBot(sql);
    }

    // eslint-disable-next-line class-methods-use-this
    async getUserPartner (userId) {
        let partner;
        const sql = 'SELECT users.id, users.shownname, partner_id, p2.id as super_partner_id, p1.firstname as partner_name, p2.firstname as super_partner_name FROM `users` '
            + 'left join partners p1 on p1.id = users.partner_id '
            + 'LEFT join partners_relations pr on pr.child_id = p1.id '
            + 'left join partners p2 on p2.id = pr.parent_id '
            + `WHERE users.id = '${userId}'`;
        await queryBot(sql).then((r) => {
            [partner] = r.data;
        });
        return partner;
    }

    // eslint-disable-next-line class-methods-use-this
    async updateUserPaid (userId, courseId, category, paid) {
        const sql = `select id, finance_info_json from user_courses where user_id = '${userId}' and course_id = '${courseId}'`;
        await queryBot(sql).then(async (r) => {
            const [row] = r.data;
            if (row) {
                const info = JSON.parse(row.finance_info_json || '{}');
                info.paid = paid;
                const infoJson = JSON.stringify(info);
                await queryBot(`update user_courses set finance_info_json = '${infoJson}' where id = '${row.id}'`);
                this.updateUserCoursePrice(userId, courseId, category, paid);
            }
        });
    }

    // eslint-disable-next-line class-methods-use-this
    async hasNewMessages (userChatId) {
        let result = false;
        await queryBot2('get-chat-new-messages-count', { chatId: userChatId }).then(async (r) => {
        // await queryBot(sql).then(async (r) => {
            const [row] = r.data;
            if (row) {
                result = +row.count > 0;
            }
        });
        return result;
    }

    // eslint-disable-next-line class-methods-use-this
    async getStorage (untiId) {
        let result;
        const sql = `select * from storage where uid = '${untiId}'`;
        await queryBot(sql).then(async (r) => {
            const [row] = r.data;
            result = row;
        });
        return result;
    }

    // eslint-disable-next-line class-methods-use-this
    normalizeFio (fio) {
        let result = '';
        const words = fio.split(' ');
        words.every((word) => {
            const capWord = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            result += `${capWord} `;
            return true;
        });
        return result.trim();
    }

    // eslint-disable-next-line class-methods-use-this
    async getUserAnswer (chatId, questionId) {
        let result;
        const sql = 'select user_answers.type, user_answers.answer, user_answers.result, task_questions.question, task_questions.answers '
            + ' from user_answers '
            + ' inner join task_questions on task_questions.id = user_answers.question_id '
            + `where user_id = '${chatId}' and question_id = '${questionId}'`;
        await queryBot(sql).then(async (r) => {
            [result] = r.data;
        });
        return result;
    }

    // eslint-disable-next-line class-methods-use-this
    async getMotivationAnswer (userId, chatId) {
        let result;
        await queryBot2('get-motivation-answer', { userId }).then(async (r) => {
            const [row] = r.data;
            if (row) {
                result = row.value;
            }
        });
        if (!result && chatId) {
            // взять первый по списку ответ клиента из ответов
            await queryBot(`select answer from user_answers where user_id = '${chatId}' and type = 'input' order by id limit 1`).then((r) => {
                const [row] = r.data;
                if (row) {
                    result = row.answer;
                    if (result) {
                        const c = {
                            userId,
                            type: 'motivationQuestion',
                            value: result,
                        };
                        this.context.$db.insertObject('users_cache', c);
                    }
                }
            });
        }
        return result;
    }

    // eslint-disable-next-line class-methods-use-this
    async updateTracePercent (userId, courseId) {
        let totalCount = 0;
        let approvedCount = 0;
        let percents = 0;
        const sql = `select count(*) as count from digital_traces where user_id = '${userId}' and course_id = '${courseId}' `;
        await queryBot(sql).then((r) => {
            totalCount = +r.data[0].count;
        });
        await queryBot(`${sql} and sent_at is not null and status = '${DIGITAL_TRACE_STATUS.Принят}'`).then((r) => {
            approvedCount = +r.data[0].count;
        });
        if (totalCount > 0) {
            percents = (100 * approvedCount) / totalCount;
            percents = percents.toFixed(0);
        }
        queryBot(`update users set tracePercent = '${percents}' where id = '${userId}'`);
        return percents;
    }
}
